import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import AccordionCDS from 'app/shared/componentsRestyled/coreDesignSystem/AccordionCDS';
import PropTypes from 'prop-types';

import { WORKSHEET_EMPTY_VALUE } from '../../WorksheetDrawer.constants';
import {
  worksheetLoanFactsAccordionStyles,
  worksheetLoanFactsRowStyles,
  worksheetLoanFactsRowsStyles,
  worksheetLoanFactsSectionStyles,
} from '../../WorksheetDrawer.styles';

import {
  WORKSHEET_LOAN_FACTS_SUBTITLE,
  WORKSHEET_LOAN_FACTS_TITLE,
} from './WorksheetLoanFactsPanel.constants';
import { WorksheetLoanFactsPanelTestIds } from './WorksheetLoanFactsPanel.testIds';

/**
 * Read-only loan facts beside (wide drawer) or above the worksheet questions, as a compact grid of
 * label-over-value pairs, collapsible. Renders nothing without sections (non-chattel loans).
 */
const WorksheetLoanFactsPanel = ({ sections = [] }) => {
  if (!sections.length) {
    return null;
  }

  return (
    <Box data-testid={WorksheetLoanFactsPanelTestIds.PANEL}>
      <AccordionCDS
        defaultExpanded
        className="mb-16"
        customStyles={worksheetLoanFactsAccordionStyles}
        title={WORKSHEET_LOAN_FACTS_TITLE}
        titleVariant="bold16"
      >
        <Typography className="mb-4" color="text.secondary" component="div" variant="regular12">
          {WORKSHEET_LOAN_FACTS_SUBTITLE}
        </Typography>

        {sections.map(({ id, title, rows }) => (
          <Box
            data-testid={WorksheetLoanFactsPanelTestIds.SECTION}
            key={id}
            sx={worksheetLoanFactsSectionStyles}
          >
            <Typography component="div" variant="bold14">
              {title}
            </Typography>

            <Box sx={worksheetLoanFactsRowsStyles}>
              {rows.map(({ label, value }) => (
                <Box
                  data-testid={WorksheetLoanFactsPanelTestIds.ROW}
                  key={label}
                  sx={worksheetLoanFactsRowStyles}
                >
                  <Typography color="text.secondary" component="span" variant="regular12">
                    {label}
                  </Typography>
                  <Typography
                    component="span"
                    data-testid={WorksheetLoanFactsPanelTestIds.VALUE}
                    variant="medium14"
                  >
                    {value ?? WORKSHEET_EMPTY_VALUE}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </AccordionCDS>
    </Box>
  );
};

WorksheetLoanFactsPanel.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      rows: PropTypes.arrayOf(
        PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.string }),
      ).isRequired,
    }),
  ),
};

export default memo(WorksheetLoanFactsPanel);
